import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { JSX } from "react";

import { CREATABLE_PLATFORMS, PLATFORM_LABELS } from "../types.js";
import type { BudgetState, Platform } from "../types.js";

/**
 * Shared atoms for the operator-driven studio surfaces (Create New / Ideas /
 * Knowledge). Kept here so the platform picker, angle picker and budget
 * formatting stay identical across the three pages.
 */

/** The content angles the agent supports, surfaced for Create New. "" = let
 *  the agent pick. */
export const ANGLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "", label: "Auto (let the agent choose)" },
  { value: "feature_spotlight", label: "Feature spotlight" },
  { value: "did_you_know", label: "Did you know?" },
  { value: "problem_solved", label: "Problem → solution" },
  { value: "news_react", label: "React to industry news" },
];

export function PlatformSelect({
  value,
  onChange,
  disabled,
  label = "Platform",
  size = "small",
}: {
  value: Platform;
  onChange: (p: Platform) => void;
  disabled?: boolean;
  label?: string;
  size?: "small" | "medium";
}): JSX.Element {
  return (
    <TextField
      select
      size={size}
      label={label}
      value={value}
      disabled={!!disabled}
      onChange={(e) => onChange(e.target.value as Platform)}
      sx={{ minWidth: 160 }}
    >
      {CREATABLE_PLATFORMS.map((p) => (
        <MenuItem key={p} value={p}>
          {PLATFORM_LABELS[p]}
        </MenuItem>
      ))}
    </TextField>
  );
}

export function AngleSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <TextField
      select
      size="small"
      label="Angle"
      value={value}
      disabled={!!disabled}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 200 }}
    >
      {ANGLE_OPTIONS.map((a) => (
        <MenuItem key={a.value || "auto"} value={a.value}>
          {a.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

export function formatUsd(n: number): string {
  // Sub-cent spend is common (estimated token cost) — show enough precision.
  if (n > 0 && n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

/** Tone for the budget bar/pill: ok < 80%, warn < 100%, over at/after the cap. */
export function budgetTone(b: BudgetState): "ok" | "warn" | "over" {
  if (b.exhausted) return "over";
  if (b.budget_usd > 0 && b.spend_usd / b.budget_usd >= 0.8) return "warn";
  return "ok";
}

export function budgetColor(t: Theme, tone: "ok" | "warn" | "over"): string {
  if (tone === "over") return t.palette.error.main;
  if (tone === "warn") return t.palette.warning.main;
  return t.palette.success.main;
}

/** "09:00 Riga"-style label for the next budget reset, in the viewer's locale. */
export function resetLabel(resetsAtIso: string): string {
  const d = new Date(resetsAtIso);
  return d.toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });
}

/** A thin spend-vs-budget bar reused by the Knowledge page + (compact) pill. */
export function BudgetBar({ budget }: { budget: BudgetState }): JSX.Element {
  const tone = budgetTone(budget);
  const pct =
    budget.budget_usd > 0
      ? Math.min(100, (budget.spend_usd / budget.budget_usd) * 100)
      : 0;
  return (
    <Box sx={{ display: "grid", gap: 0.5 }}>
      <Box
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: (t) => alpha(t.palette.text.secondary, 0.12),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 999,
            bgcolor: (t) => budgetColor(t, tone),
            transition: "width 300ms",
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {formatUsd(budget.spend_usd)}
        {budget.budget_usd > 0 ? ` / ${formatUsd(budget.budget_usd)}` : " (no cap)"} ·{" "}
        {budget.exhausted
          ? `sleeping until ${resetLabel(budget.resets_at)}`
          : `resets ${resetLabel(budget.resets_at)}`}
      </Typography>
    </Box>
  );
}
